import type { FieldDefinition } from '@cloud/custom-modules'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { toast } from 'sonner'
import { EntityForm } from '@/components/custom/entity-form'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { errorMessage } from '@/lib/error-message'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/m/$moduleKey/$entityKey/new')({
  component: NewRecordPage,
})

function NewRecordPage() {
  const { moduleKey, entityKey } = Route.useParams()
  const t = useTranslations()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: module } = useQuery(
    orpc.modules.custom.modules.get.queryOptions({ input: { key: moduleKey } }),
  )

  const { data: entity, isLoading } = useQuery(
    orpc.modules.custom.entities.get.queryOptions({
      input: { moduleKey, key: entityKey },
    }),
  )

  const createMutation = useMutation(
    orpc.modules.custom.records.create.mutationOptions({
      onSuccess: (record) => {
        toast.success(`${entity?.name ?? 'Record'} created`)
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.records.list.queryKey({
            input: { moduleKey, entityKey },
          }),
        })
        void navigate({
          to: '/m/$moduleKey/$entityKey/$recordId',
          params: { moduleKey, entityKey, recordId: record.id },
        })
      },
      onError: (err) => toast.error(errorMessage(err, t)),
    }),
  )

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!entity) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">Entity not found</h1>
      </div>
    )
  }

  const pageTitle = t('entityRuntime.actions.new', { entity: entity.name })

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        breadcrumbs={[
          { label: module?.name ?? moduleKey, to: '/m/$moduleKey', params: { moduleKey } },
          {
            label: entity.namePlural ?? entity.name,
            to: '/m/$moduleKey/$entityKey',
            params: { moduleKey, entityKey },
          },
          { label: pageTitle },
        ]}
        title={pageTitle}
      />

      <Card>
        <CardContent className="pt-6">
          <EntityForm
            fields={entity.fields as FieldDefinition[]}
            sections={
              (
                entity as unknown as {
                  form?: {
                    sections?: { title?: string; description?: string; fields: string[] }[]
                  }
                }
              ).form?.sections
            }
            submitting={createMutation.isPending}
            submitLabel={`Create ${entity.name}`}
            onCancel={() =>
              navigate({
                to: '/m/$moduleKey/$entityKey',
                params: { moduleKey, entityKey },
              })
            }
            onSubmit={async (data) => {
              await createMutation.mutateAsync({ moduleKey, entityKey, data })
            }}
          />
        </CardContent>
      </Card>
    </div>
  )
}
